import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button as AntButton,
  Drawer,
  InputNumber,
  Radio,
  Spin,
  Tag,
  Tooltip,
  notification,
} from "antd";
import cuid from "cuid";
import { get, isNil } from "lodash";
import PropTypes from "prop-types";
import {
  MdAdd,
  MdArrowUpward,
  MdClose,
  MdImage,
  MdCheck,
  MdCode,
  MdOutlineHighlightAlt,
  MdOutlineLanguage,
  MdRocketLaunch,
  MdViewList,
} from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import { useMutation } from "shared/hooks/useApi";
import useScreenJob from "shared/hooks/useScreenJob";
import graphql from "utils/api/graphql";
import { createScreenDraft, readFileAsBase64 } from "utils/helpers/screenDraft";
import ScreenProposalPreview, {
  hasSlotCollapse,
} from "./ScreenProposalPreview";
import SlotInspector from "../../ScreenStudios/Detail/SlotInspector";
import ConditionListEditor from "./ConditionListEditor";
import SlotList from "../../ScreenStudios/Detail/SlotList";
import ScreenThumbnail from "./ScreenThumbnail";
import { conditionSummary, DEFAULT_STATE_PREVIEW_KEY } from "./catalog";
import useScreenRevision from "./useScreenRevision";
import useScreenWrites from "./useScreenWrites";

/**
 * Full-width workspace for one screen: the conversation drives the model on the
 * left, the screen it acts on stays visible on the right.
 *
 * It opens over the node drawer rather than inside its Screen tab, which shares
 * room with four other tabs — two columns there would be unreadable, and
 * widening the node drawer would degrade the tabs that do not need the space.
 */
// Les trois vocabulaires du composant. Ecrits une fois plutot qu a chaque
// appel : chacun compte une poignee de membres pour des dizaines de sites, et
// une faute de frappe y est silencieuse.
const MODES = { PREVIEW: "preview", CODE: "code", SLOTS: "slots" };
const VOICE = { OPERATOR: "user", WORKSHOP: "assistant", MODEL: "model" };
const PROPOSAL_KIND = { STATE: "state", VERSION: "version" };

const ScreenWorkshop = ({
  open,
  processId,
  onClose,
  title,
  screenVersionId,
  statusId,
  screenId,
  versionNumber,
  htmlContent,
  styleContent,
  slots,
  manifestId,
  states,
  variables,
  conditionLabels,
  published,
  publishBlocked,
  labels,
  reload,
}) => {
  const [activeKey, setActiveKey] = useState(DEFAULT_STATE_PREVIEW_KEY);
  const [mode, setMode] = useState(MODES.PREVIEW);
  const [selectedSlot, setSelectedSlot] = useState(null);
  // Seeded from the active state, not derived from it: editing a condition
  // must survive a refresh triggered by something else in the panel.
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Seeded when the target or the mode changes, never on every refresh: a
  // reload while editing would silently drop what is being typed.
  const [code, setCode] = useState({ html: "", css: "" });
  const [savingCode, setSavingCode] = useState(false);
  // Where the next instruction goes, kept apart from what is displayed: one
  // correction often concerns several variants at once — a header, a footer,
  // a data-field forgotten everywhere — while only one can be on screen.
  const [targets, setTargets] = useState([DEFAULT_STATE_PREVIEW_KEY]);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  // Creating a state needs a field and a value: the model must never decide
  // how a screen is split, that is a product choice.
  const [newState, setNewState] = useState(null);
  const [generating, setGenerating] = useState(false);
  // Held until the send button is pressed: the operator often writes an
  // instruction to go with the mockup, and firing on file choice would send
  // the image before that instruction exists.
  const [attachment, setAttachment] = useState(null);
  // Picking arms the preview; focus is what was picked. Kept apart so the
  // screen stops reacting to clicks once one element has been chosen.
  const [picking, setPicking] = useState(false);
  const [focused, setFocused] = useState([]);
  // A capture produces a body and, when the board shows several variants,
  // its states. All of them travel together: applying the body without its
  // states would leave a screen that no longer matches what was reviewed.
  const [generated, setGenerated] = useState([]);
  const [applyingGenerated, setApplyingGenerated] = useState(false);
  const fileInputRef = useRef(null);
  const threadRef = useRef(null);

  const {
    generateHtml: generateScreen,
    generateState,
    revise,
  } = useScreenJob({ active: open });

  const { proposals, revising, applying, propose, apply, discard } =
    useScreenRevision({
      screenVersionId,
      states,
      defaultLabel: labels.TARGET_VERSION || labels.PREVIEW_DEFAULT,
      revise,
    });
  const [addScreen] = useMutation(graphql.mutations.ADD_SCREEN);
  const [addScreenVersion] = useMutation(graphql.mutations.ADD_SCREEN_VERSION);
  const [updateState] = useMutation(graphql.mutations.UPDATE_SCREEN_STATE);
  const [detectSlots] = useMutation(graphql.mutations.DETECT_SLOTS);
  const { writeVersion, writeState, createState } = useScreenWrites();
  const [publishVersion, { loading: publishing }] = useMutation(
    graphql.mutations.PUBLISH_SCREEN_VERSION,
  );
  const [deleteState] = useMutation(graphql.mutations.DELETE_SCREEN_STATE);
  const [updateManifest] = useMutation(
    graphql.mutations.UPDATE_BINDING_MANIFEST,
  );
  const [detectForState] = useMutation(
    graphql.mutations.DETECT_SLOTS_FOR_STATE,
  );

  const shownProposals = proposals.length ? proposals : generated;
  const pending = shownProposals.length > 0;

  const ordered = useMemo(
    () => [...(states || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [states],
  );

  const active =
    activeKey === DEFAULT_STATE_PREVIEW_KEY
      ? null
      : ordered.find((state) => state.id === activeKey);

  const shownHtml = active ? active.htmlContent : htmlContent;
  const shownSlots = active
    ? get(active, "bindingManifest.slots") || []
    : slots || [];

  const defaultLabel = labels.TARGET_VERSION || labels.PREVIEW_DEFAULT;

  const shownManifestId = active
    ? get(active, "bindingManifest.id")
    : manifestId;

  /**
   * Detection classifies a slot from the markup alone and sometimes gets it
   * wrong. Correcting the binding by hand is the only way out that does not
   * involve rewriting HTML, so it belongs where the screen is being worked on.
   */
  const handleSlotUpdate = async (slotId, binding) => {
    if (!shownManifestId) return;
    await updateManifest({
      variables: {
        where: { id: shownManifestId },
        data: {
          slots: shownSlots.map((slot) =>
            slot.id === slotId ? { ...slot, binding } : slot,
          ),
        },
      },
    });
    if (reload) reload();
  };

  useEffect(() => {
    if (mode !== MODES.CODE) return;
    setCode({
      html: (active ? active.htmlContent : htmlContent) || "",
      css: styleContent || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, activeKey]);

  useEffect(() => {
    setSettings(
      active
        ? {
            name: active.name || "",
            // Read from the state, not defaulted: order decides which of two
            // states wins when both conditions match, so a box showing 0 over
            // a stored 3 would invite the operator to overwrite it.
            order: active.order,
            conditions: active.conditions || [],
          }
        : null,
    );
    setSelectedSlot(null);
    setSettingsOpen(false);
    // Slot ids are local to their target: slot_012 is one element in the
    // version and an unrelated one in a state. Carrying a pick across a
    // switch would ring something nobody chose, under a label naming what
    // was left behind — and send an instruction about it.
    setFocused([]);
    setPicking(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  // A target can vanish under the workshop: a state deleted here, or a
  // reload bringing back a screen without it. Left alone, the workshop keeps
  // pointing at an id the API cannot resolve — the correction fails, and the
  // thread names the target by its raw cuid because no state answers to it.
  useEffect(() => {
    const keys = [
      DEFAULT_STATE_PREVIEW_KEY,
      ...ordered.map((state) => state.id),
    ];
    if (keys.indexOf(activeKey) < 0) setActiveKey(DEFAULT_STATE_PREVIEW_KEY);
    setTargets((current) => {
      const kept = current.filter((key) => keys.indexOf(key) >= 0);
      if (kept.length === current.length) return current;
      return kept.length ? kept : [DEFAULT_STATE_PREVIEW_KEY];
    });
  }, [ordered, activeKey]);

  useEffect(() => {
    const node = threadRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  const say = (role, text) =>
    setMessages((current) => [
      ...current,
      { role, text, id: `${Date.now()}${current.length}` },
    ]);

  // A plain click holds one element and drops the rest; Ctrl/Cmd+click adds.
  // Clicking a held element releases it either way, so the same gesture takes
  // and gives back rather than sending the operator after a small cross.
  const focusOn = (slotId, additive) => {
    const slot = shownSlots.find((item) => item.id === slotId);
    const picked = {
      id: slotId,
      label:
        get(slot, "label") ||
        get(slot, "binding.sourceValue") ||
        get(slot, "type") ||
        slotId,
    };
    setFocused((current) => {
      if (current.some((item) => item.id === slotId)) {
        return current.filter((item) => item.id !== slotId);
      }
      return additive ? [...current, picked] : [picked];
    });

    // The instruction names an anchor that only exists on the screen it was
    // picked from: slot_012 is one element here and another one in a state.
    // Sending it anywhere else would correct something nobody pointed at, so
    // the target follows the pick — announced, not slipped in.
    if (targets.length !== 1 || targets[0] !== activeKey) {
      setTargets([activeKey]);
      say(VOICE.WORKSHOP, `${labels.TARGET_PINNED} ${nameOfTarget(activeKey)}`);
    }
  };

  const nameOfTarget = (key) => {
    if (key === DEFAULT_STATE_PREVIEW_KEY) return defaultLabel;
    const state = ordered.find((item) => item.id === key);
    return get(state, "name") || key;
  };

  const chips = [
    {
      key: DEFAULT_STATE_PREVIEW_KEY,
      name: defaultLabel,
      condition: null,
    },
    ...ordered.map((state) => ({
      key: state.id,
      name: state.name || state.id,
      condition: conditionSummary(state.conditions),
    })),
  ];

  const reviseTargets = async (text) => {
    say(
      VOICE.WORKSHOP,
      `${labels.THINKING} ${targets.map(nameOfTarget).join(", ")}…`,
    );
    const { produced, failed } = await propose(targets, text);
    setMessages((current) => current.slice(0, -1));

    if (failed && failed.length) {
      say(VOICE.WORKSHOP, `${labels.REVISE_ERROR} : ${failed.join(", ")}`);
      notification.error({
        message: `${labels.REVISE_ERROR} : ${failed.join(", ")}`,
      });
    }
    for (const proposal of produced || []) {
      const drop = hasSlotCollapse(proposal)
        ? ` ⚠ ${labels.COLLAPSE_SHORT}`
        : "";
      say(
        VOICE.WORKSHOP,
        `${labels.PROPOSAL_READY} ${proposal.targetName} — ${proposal.slotsBefore} → ${proposal.slotsAfter} ${labels.SLOT_COUNT}${drop}`,
      );
      // The model's own account of the edit, kept next to the measured
      // counts rather than instead of them: it says what was intended, the
      // numbers say what was produced.
      if (proposal.summary) say(VOICE.MODEL, `« ${proposal.summary} »`);
    }
  };

  /**
   * A mockup either describes the default body or one new variant. Which of
   * the two is never inferred from the image: the operator says so by leaving
   * the default screen selected, or by picking the new-state chip. What
   * triggers the variant is deduced from the note and the mockup, and comes
   * back inside the proposal, where it is read before anything is written.
   */
  const generateFromCapture = async (file, text) => {
    const wantsState = !isNil(newState);
    if (!wantsState && activeKey !== DEFAULT_STATE_PREVIEW_KEY) {
      say(VOICE.WORKSHOP, labels.CAPTURE_TARGET_HINT);
      return;
    }

    setGenerating(true);
    try {
      const image = await readFileAsBase64(file);
      const payload = { data: image, mediaType: file.type };

      if (wantsState) {
        const produced = await generateState({
          screenVersion: { id: screenVersionId },
          image: payload,
          instructions: text || undefined,
        });
        if (!produced) return;
        setGenerated([
          {
            kind: PROPOSAL_KIND.STATE,
            targetName: produced.name,
            htmlContent: produced.htmlContent,
            conditions: produced.conditions,
            order: produced.order,
            slotsBefore: 0,
            slotsAfter: produced.slotsAfter,
          },
        ]);
        say(
          VOICE.WORKSHOP,
          `${labels.PROPOSAL_READY} ${produced.name} — ${produced.slotsAfter} ${labels.SLOT_COUNT}`,
        );
        if (produced.summary) say(VOICE.MODEL, `« ${produced.summary} »`);
        return;
      }

      const produced = await generateScreen({
        image: payload,
        instructions: text || undefined,
      });
      if (!produced) return;
      const nextOrder = (states || []).reduce(
        (max, item) => Math.max(max, item.order || 0),
        0,
      );
      setGenerated([
        {
          kind: PROPOSAL_KIND.VERSION,
          targetName: defaultLabel,
          htmlContent: produced.html,
          styleContent: produced.css,
          previousHtmlContent: htmlContent,
          slotsBefore: (slots || []).length,
          slotsAfter: produced.slotsAfter,
        },
      ]);
      const variants = produced.states || [];
      const extra = variants.length
        ? ` · ${variants.length} ${labels.STATES_FOUND}`
        : "";
      say(
        VOICE.WORKSHOP,
        `${labels.PROPOSAL_READY} ${defaultLabel} — ${(slots || []).length} → ${produced.slotsAfter} ${labels.SLOT_COUNT}${extra}`,
      );
      if (produced.summary) say(VOICE.MODEL, `« ${produced.summary} »`);

      for (let index = 0; index < variants.length; index++) {
        const variant = variants[index];
        try {
          const drawn = await generateState({
            image: payload,
            styleContent: produced.css,
            htmlContent: produced.html,
            conditionField: variant.conditionField || undefined,
            conditionValue: variant.conditionValue || undefined,
            instructions: text || undefined,
          });
          if (!drawn) return;
          setGenerated((current) => [
            ...current,
            {
              kind: PROPOSAL_KIND.STATE,
              targetName: variant.name || drawn.name,
              htmlContent: drawn.htmlContent,
              conditions: variant.conditions || drawn.conditions,
              order: nextOrder + index + 1,
              slotsBefore: 0,
              slotsAfter: drawn.slotsAfter,
            },
          ]);
          say(
            VOICE.WORKSHOP,
            `${labels.PROPOSAL_READY} ${variant.name || drawn.name} — ${drawn.slotsAfter} ${labels.SLOT_COUNT}`,
          );
        } catch (_error) {
          say(
            VOICE.WORKSHOP,
            `${labels.GENERATE_ERROR} : ${variant.name || index + 1}`,
          );
        }
      }
    } catch (error) {
      const message = get(error, "reason") || labels.GENERATE_ERROR;
      say(VOICE.WORKSHOP, message);
      notification.error({ message });
    } finally {
      setGenerating(false);
    }
  };

  const busy = revising || applying || generating || applyingGenerated;

  const handleSend = async () => {
    const text = draft.trim();
    if (busy || pending) return;
    if (!text && !attachment) return;
    if (!attachment && !screenVersionId) {
      say(VOICE.WORKSHOP, labels.NO_SCREEN_YET);
      return;
    }
    if (!attachment && !targets.length) {
      say(VOICE.WORKSHOP, labels.TARGETS_EMPTY);
      return;
    }

    const about = focused.length
      ? `${labels.ABOUT} ${focused
          .map((item) => `« ${item.label} »`)
          .join(", ")}`
      : "";
    say(
      VOICE.OPERATOR,
      attachment
        ? `${labels.CAPTURE_SENT} ${attachment.name}${text ? ` — ${text}` : ""}`
        : `${about ? `${about} — ` : ""}${text}`,
    );
    setDraft("");
    const file = attachment ? attachment.file : null;
    const picked = focused;
    setAttachment(null);
    setFocused([]);
    setPicking(false);

    if (file) {
      await generateFromCapture(file, text);
      return;
    }
    // The element travels with the instruction rather than as a separate
    // parameter: the model already reads the markup, and naming the anchor it
    // must stay inside is what keeps a local correction local.
    await reviseTargets(
      picked.length
        ? `${(labels.SCOPE_INSTRUCTION || "").replace(
            "{slots}",
            picked
              .map(
                (item) => `data-kayros-slot="${item.id}" (« ${item.label} »)`,
              )
              .join(", "),
          )} ${text}`
        : text,
    );
  };
  /**
   * Which proposals already produced a row, and under which id.
   *
   * A ref rather than state: applying re-renders, and this must survive that
   * without causing one. It is keyed by the proposal object itself, which is
   * stable for as long as the list is shown.
   */
  const createdStates = useRef(new Map());

  /**
   * Writing and re-detecting stay one gesture whatever produced the markup:
   * in between, a screen holds slots pointing at markup that no longer exists.
   *
   * A retry is the delicate case. If the row was created and only detection
   * failed, creating again under a fresh id would leave two states where the
   * operator asked for one — and nothing downstream could tell them apart.
   * So a proposal that already landed is updated, never re-created.
   */
  const applyOneGenerated = async (item) => {
    if (item.kind === PROPOSAL_KIND.STATE) {
      const created = createdStates.current.get(item);
      if (created) {
        await writeState({ id: created, htmlContent: item.htmlContent });
        return;
      }
      await createState({
        id: cuid(),
        screenVersionId,
        name: item.targetName,
        order: item.order,
        htmlContent: item.htmlContent,
        conditions: item.conditions,
        onCreated: (id) => createdStates.current.set(item, id),
      });
      return;
    }
    await writeVersion({
      id: screenVersionId,
      htmlContent: item.htmlContent,
      styleContent: item.styleContent,
    });
  };

  /**
   * On a blank node there is no version to write into: the body and its
   * states are created together, in one draft. Writing the body first and
   * adding the states afterwards would leave a screen briefly published
   * without the variants that were reviewed with it.
   */
  const createFromGenerated = async (list) => {
    const body = list.find((item) => item.kind === PROPOSAL_KIND.VERSION);
    const stateItems = list.filter((item) => item.kind === PROPOSAL_KIND.STATE);
    await createScreenDraft({
      addScreen,
      addScreenVersion,
      detectSlots,
      statusId,
      screenId,
      description: title || labels.MANUAL_SCREEN,
      versionNumber,
      htmlContent: get(body, "htmlContent", ""),
      styleContent: get(body, "styleContent", ""),
      // The helper stores states by their own shape: a proposal names its
      // target for display, the entity needs a name column.
      states: stateItems.map((item) => ({
        name: item.targetName,
        order: item.order,
        htmlContent: item.htmlContent,
        conditions: item.conditions || [],
      })),
      copyStates: stateItems.length > 0,
    });
  };

  // The body is written before its states so detection on the version does
  // not run against markup the states are about to replace.
  const applyGenerated = async (list) => {
    if (!screenVersionId) {
      await createFromGenerated(list);
      setGenerated([]);
      return;
    }
    // Each item leaves the list the moment it lands. Nothing spans these
    // mutations as a transaction, so a failure on the third state used to
    // leave the first two written AND still listed: clicking "Appliquer"
    // again created them a second time, under fresh ids that nothing could
    // reconcile with the ones already there.
    for (const item of list) {
      await applyOneGenerated(item);
      setGenerated((current) => current.filter((entry) => entry !== item));
    }
  };

  const handleApply = async (kept) => {
    const retained = kept || shownProposals;
    const applied = retained.map((item) => item.targetName).join(", ");

    if (generated.length) {
      setApplyingGenerated(true);
      try {
        await applyGenerated(retained);
        say(VOICE.WORKSHOP, `${labels.APPLIED} ${applied}`);
        setNewState(null);
      } catch (error) {
        say(VOICE.WORKSHOP, labels.GENERATE_ERROR);
        notification.error({ message: labels.GENERATE_ERROR });
      } finally {
        setApplyingGenerated(false);
      }
      if (reload) reload();
      return;
    }

    const { failed } = await apply(retained);
    if (failed && failed.length) {
      say(VOICE.WORKSHOP, `${labels.REVISE_ERROR} : ${failed.join(", ")}`);
    } else {
      say(VOICE.WORKSHOP, `${labels.APPLIED} ${applied}`);
    }
    if (reload) reload();
  };

  /**
   * Saving writes and re-detects in one step, like every other path: a screen
   * must never hold slots that point at markup it no longer contains.
   */
  const handleSaveCode = async () => {
    setSavingCode(true);
    try {
      // A blank node has nothing to write into. Pasted markup is then the
      // first version itself, not an edit of one: an integrator handing over
      // a page has no mockup to photograph, and requiring one would make the
      // capture the only door into the workshop.
      if (!screenVersionId) {
        await createScreenDraft({
          addScreen,
          addScreenVersion,
          detectSlots,
          statusId,
          screenId,
          // Names the screen after the node it belongs to: the studio lists
          // screens by this field, and one created without it shows a dash
          // among rows that all look alike.
          description: title || labels.MANUAL_SCREEN,
          versionNumber,
          htmlContent: code.html,
          styleContent: code.css,
        });
      } else if (active) {
        await writeState({ id: active.id, htmlContent: code.html });
      } else {
        // keepEmptyStyle: an operator who empties the CSS pane means it. The
        // model's path defaults the other way, where an empty sheet means
        // "untouched".
        await writeVersion({
          id: screenVersionId,
          htmlContent: code.html,
          styleContent: code.css,
          keepEmptyStyle: true,
        });
      }
      say(VOICE.WORKSHOP, `${labels.CODE_SAVED} ${nameOfTarget(activeKey)}`);
      if (reload) reload();
    } catch (error) {
      say(VOICE.WORKSHOP, labels.CODE_SAVE_ERROR);
      notification.error({ message: labels.CODE_SAVE_ERROR });
    } finally {
      setSavingCode(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!active || !settings) return;
    setSavingSettings(true);
    try {
      await updateState({
        variables: {
          where: { id: active.id },
          data: {
            name: settings.name,
            order: settings.order,
            conditions: settings.conditions || [],
          },
        },
      });
      say(VOICE.WORKSHOP, `${labels.STATE_SAVED} ${settings.name}`);
      if (reload) reload();
    } catch (error) {
      say(VOICE.WORKSHOP, labels.SAVE_ERROR);
      notification.error({ message: labels.SAVE_ERROR });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDeleteState = async () => {
    if (!active) return;
    const name = active.name || active.id;
    try {
      await deleteState({ variables: { where: { id: active.id } } });
      setActiveKey(DEFAULT_STATE_PREVIEW_KEY);
      say(VOICE.WORKSHOP, `${labels.STATE_DELETED} ${name}`);
      if (reload) reload();
    } catch (error) {
      say(VOICE.WORKSHOP, labels.SAVE_ERROR);
      notification.error({ message: labels.SAVE_ERROR });
    }
  };

  /**
   * Detection normally runs with every write. Offering it alone matters when
   * the markup was changed elsewhere — the states table, the HTML editor —
   * and the manifest has fallen behind without anything saying so.
   */
  const handleDetect = async () => {
    try {
      if (active) {
        await detectForState({ variables: { screenState: { id: active.id } } });
      } else {
        await detectSlots({
          variables: { screenVersion: { id: screenVersionId } },
        });
      }
      say(VOICE.WORKSHOP, `${labels.DETECTED} ${nameOfTarget(activeKey)}`);
      if (reload) reload();
    } catch (error) {
      say(VOICE.WORKSHOP, labels.DETECT_ERROR);
      notification.error({ message: labels.DETECT_ERROR });
    }
  };

  const handlePublish = async () => {
    try {
      await publishVersion({
        variables: { where: { id: screenVersionId } },
      });
      say(VOICE.WORKSHOP, labels.PUBLISHED);
      if (reload) reload();
    } catch (error) {
      say(VOICE.WORKSHOP, labels.PUBLISH_ERROR);
      notification.error({ message: labels.PUBLISH_ERROR });
    }
  };

  const handleDiscard = () => {
    discard();
    setGenerated([]);
    say(VOICE.WORKSHOP, labels.DISCARDED);
  };

  const close = () => {
    discard();
    setGenerated([]);
    setNewState(null);
    setMessages([]);
    if (onClose) onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      width="96%"
      title={title}
      destroyOnClose
      rootClassName="screen-workshop-drawer"
    >
      <div className="screen-workshop">
        <div className="screen-workshop__conversation">
          <div className="screen-workshop__panel-title">
            {labels.CONVERSATION}
          </div>

          <div className="screen-workshop__thread" ref={threadRef}>
            {messages.length ? (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`screen-workshop__msg screen-workshop__msg--${message.role}`}
                >
                  {message.text}
                </div>
              ))
            ) : (
              <div className="canvas-editor__meta">
                {labels.CONVERSATION_HINT}
              </div>
            )}
            {busy ? (
              <div className="screen-workshop__working">
                <Spin size="small" />
                <span>{labels.WORKING}</span>
              </div>
            ) : null}
          </div>

          <div className="screen-workshop__composer">
            {/* What a message will touch belongs where the message is
                written: the capture and the picked elements already sit
                here, and the targets were the same answer to the same
                question, kept a panel away in another vocabulary. */}
            <div className="screen-workshop__targets">
              <span className="screen-workshop__targets-title">
                {labels.TARGETS_TITLE}
              </span>
              {chips.map((chip) => {
                const on = targets.indexOf(chip.key) >= 0;
                return (
                  <button
                    key={chip.key}
                    type="button"
                    className={`screen-workshop__chip${
                      on ? " screen-workshop__chip--on" : ""
                    }`}
                    disabled={busy || pending || !!attachment || !!newState}
                    onClick={() =>
                      setTargets(
                        on
                          ? targets.filter((key) => key !== chip.key)
                          : [...targets, chip.key],
                      )
                    }
                  >
                    {on ? <MdCheck size={13} /> : null}
                    {chip.name}
                  </button>
                );
              })}
            </div>
            {attachment && !newState ? (
              <div className="canvas-editor__meta mb-8">
                {labels.TARGETS_IGNORED}
              </div>
            ) : null}

            {newState ? (
              <div className="screen-workshop__targets-new">
                <div className="screen-workshop__attachment">
                  <MdAdd size={16} />
                  <span>{labels.NEW_STATE}</span>
                  <button
                    type="button"
                    onClick={() => setNewState(null)}
                    title={labels.PROPOSAL?.CANCEL}
                  >
                    <MdClose size={14} />
                  </button>
                </div>
                <div className="canvas-editor__meta">
                  {labels.NEW_STATE_HINT}
                </div>
              </div>
            ) : null}

            {focused.map((item, index) => (
              <div key={item.id} className="screen-workshop__attachment">
                {focused.length > 1 ? (
                  <span className="screen-workshop__focus-rank">
                    {index + 1}
                  </span>
                ) : (
                  <MdOutlineHighlightAlt size={16} />
                )}
                {/* The anchor still travels with the instruction; only the
                    operator is spared it, since the label already names
                    the element that is held. */}
                <span title={item.id}>{`« ${item.label} »`}</span>
                <button
                  type="button"
                  onClick={() =>
                    setFocused((current) =>
                      current.filter((entry) => entry.id !== item.id),
                    )
                  }
                  title={labels.PROPOSAL?.CANCEL}
                >
                  <MdClose size={14} />
                </button>
              </div>
            ))}
            {attachment ? (
              <div className="screen-workshop__attachment">
                <MdImage size={16} />
                <span>{attachment.name}</span>
                <button
                  type="button"
                  onClick={() => setAttachment(null)}
                  title={labels.PROPOSAL?.CANCEL}
                >
                  <MdClose size={14} />
                </button>
              </div>
            ) : null}

            <textarea
              className="screen-workshop__input"
              rows={3}
              placeholder={labels.REVISE_INSTRUCTIONS}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              // Enter sends, Shift+Enter breaks the line: the composer is a
              // message box, and reaching for the mouse after every
              // instruction breaks the rhythm of a conversation.
              onKeyDown={(e) => {
                if (e.key !== "Enter" || e.shiftKey) return;
                e.preventDefault();
                handleSend();
              }}
              disabled={busy || pending}
            />

            <div className="screen-workshop__composer-row">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="canvas-editor__file"
                onChange={(event) => {
                  const file = event.target.files && event.target.files[0];
                  event.target.value = null;
                  if (file) setAttachment({ file, name: file.name });
                }}
              />
              <Tooltip title={labels.CAPTURE}>
                <button
                  type="button"
                  className="screen-workshop__attach"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy || pending}
                >
                  <MdAdd size={18} />
                </button>
              </Tooltip>
              <Tooltip title={labels.SEND}>
                <button
                  type="button"
                  className="screen-workshop__send"
                  onClick={handleSend}
                  disabled={
                    (!draft.trim() && !attachment) ||
                    (!attachment && !targets.length) ||
                    busy ||
                    pending
                  }
                >
                  <MdArrowUpward size={18} />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="screen-workshop__canvas">
          <div className="screen-workshop__toolbar">
            <Radio.Group
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              optionType="button"
              disabled={pending || busy}
            >
              <Radio.Button value={MODES.PREVIEW}>
                <Tooltip title={labels.MODE_PREVIEW}>
                  <MdOutlineLanguage size={16} />
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={MODES.CODE}>
                <Tooltip title={labels.MODE_CODE}>
                  <MdCode size={16} />
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={MODES.SLOTS}>
                <Tooltip title={labels.MODE_SLOTS}>
                  <MdViewList size={16} />
                </Tooltip>
              </Radio.Button>
            </Radio.Group>
            {/* Left of the spacer, beside the views it acts on: arming the
                picker is part of looking at the screen, while Detecter and
                Publier act on what has been decided. */}
            {mode === MODES.PREVIEW ? (
              <Tooltip title={labels.PICK_HINT}>
                <AntButton
                  className="screen-workshop__pick"
                  type={picking ? "primary" : "default"}
                  icon={<MdOutlineHighlightAlt />}
                  disabled={!screenVersionId || pending || busy}
                  onClick={() => setPicking((current) => !current)}
                >
                  {labels.PICK_ELEMENT}
                </AntButton>
              </Tooltip>
            ) : null}
            <div className="screen-workshop__toolbar-spacer" />
            {/* Next to Detecter, which is what changes it. Hidden while a
                proposal is up: the canvas then shows markup that is not the
                screen, and the proposal carries its own counts. */}
            {!pending ? (
              <Tag color={shownSlots.length ? "default" : "warning"}>
                {`${shownSlots.length} ${labels.SLOT_COUNT}`}
              </Tag>
            ) : null}
            {published ? (
              <span className="canvas-editor__meta">
                {labels.ALREADY_PUBLISHED}
              </span>
            ) : null}
            {publishBlocked ? (
              <span className="canvas-editor__meta">
                {labels.PUBLISH_BLOCKED}
              </span>
            ) : null}
            <AntButton
              type="primary"
              icon={<MdRocketLaunch />}
              loading={publishing}
              // A pending proposal is not written yet: publishing here would
              // ship the version the operator is in the middle of replacing.
              disabled={
                !screenVersionId ||
                published ||
                publishBlocked ||
                pending ||
                busy
              }
              onClick={handlePublish}
            >
              {labels.PUBLISH}
            </AntButton>
          </div>

          <div className="screen-workshop__states">
            {chips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                className={`screen-workshop__chip${
                  chip.key === activeKey ? " screen-workshop__chip--active" : ""
                }`}
                // A pending proposal targets the state on screen: switching
                // would leave a result attached to something no longer shown,
                // and a call in flight is about to produce one.
                disabled={pending || busy}
                onClick={() => setActiveKey(chip.key)}
              >
                {chip.name}
                {chip.condition ? (
                  <span className="screen-workshop__chip-condition">
                    {chip.condition}
                  </span>
                ) : null}
              </button>
            ))}
            <button
              type="button"
              className={`screen-workshop__chip screen-workshop__chip--add${
                newState ? " screen-workshop__chip--active" : ""
              }`}
              disabled={pending || busy}
              onClick={() => setNewState((current) => (current ? null : true))}
            >
              {`+ ${labels.NEW_STATE}`}
            </button>
          </div>

          {mode === MODES.SLOTS && active && settings ? (
            <div className="screen-workshop__state-settings">
              <AntButton
                size="small"
                onClick={() => setSettingsOpen(!settingsOpen)}
              >
                {settingsOpen
                  ? labels.STATE_SETTINGS_HIDE
                  : labels.STATE_SETTINGS}
              </AntButton>
              {settingsOpen ? (
                <>
                  <div className="screen-workshop__state-row">
                    <Input
                      label={labels.NAME}
                      placeholder={labels.NAME}
                      value={settings.name}
                      onChange={(e) =>
                        setSettings((current) => ({
                          ...current,
                          name: e.target.value,
                        }))
                      }
                    />
                    <div className="screen-workshop__order">
                      <div className="canvas-editor__label">{labels.ORDER}</div>
                      <InputNumber
                        min={0}
                        value={settings.order}
                        onChange={(value) =>
                          setSettings((current) => ({
                            ...current,
                            order: isNil(value) ? 0 : value,
                          }))
                        }
                      />
                    </div>
                    <Button
                      type="primary--outlined"
                      loading={savingSettings}
                      disabled={busy || pending}
                      onClick={handleSaveSettings}
                    >
                      {labels.STATE_SETTINGS_SAVE}
                    </Button>
                    <Button
                      type="danger--outlined"
                      disabled={busy || pending}
                      onClick={handleDeleteState}
                    >
                      {labels.DELETE}
                    </Button>
                  </div>
                  <div className="canvas-editor__label">
                    {labels.CONDITIONS}
                  </div>
                  <ConditionListEditor
                    conditions={settings.conditions || []}
                    variables={variables}
                    labels={conditionLabels}
                    onChange={(next) =>
                      setSettings((current) => ({
                        ...current,
                        conditions: next,
                      }))
                    }
                  />
                </>
              ) : null}
            </div>
          ) : null}

          {pending ? (
            <ScreenProposalPreview
              proposals={shownProposals}
              styleContent={styleContent}
              labels={labels.PROPOSAL || {}}
              applying={applying || applyingGenerated || generating}
              onApply={handleApply}
              onCancel={handleDiscard}
            />
          ) : (
            <>
              {!shownSlots.length ? (
                <div className="screen-workshop__meta">
                  <span className="canvas-editor__meta">{labels.NO_SLOTS}</span>
                </div>
              ) : null}
              {mode === MODES.SLOTS ? (
                <>
                  {/* Detection rewrites this list and nothing else. Run
                      automatically after every write, it is a repair rather
                      than a step, and it belongs next to what it repairs. */}
                  <div className="screen-workshop__slots-actions">
                    <AntButton
                      disabled={!screenVersionId || pending || busy}
                      onClick={handleDetect}
                    >
                      {labels.DETECT}
                    </AntButton>
                  </div>
                  <div className="screen-workshop__slots">
                    {/* The registry is scoped by process: without it the
                        rows fall back to raw ids and the binding pickers
                        open with nothing to pick. */}
                    <SlotList
                      slots={shownSlots}
                      selectedSlot={selectedSlot}
                      onSelect={setSelectedSlot}
                      processId={processId}
                    />
                    <div className="screen-workshop__slots-right">
                      <ScreenThumbnail
                        html={shownHtml || ""}
                        styleContent={styleContent}
                        size="lg"
                        scrollable
                        highlightSlot={selectedSlot}
                        onSlotSelect={setSelectedSlot}
                        title={title}
                      />
                      <SlotInspector
                        slot={
                          selectedSlot
                            ? shownSlots.find(
                                (item) => item.id === selectedSlot,
                              )
                            : null
                        }
                        onSlotUpdate={handleSlotUpdate}
                        onClose={() => setSelectedSlot(null)}
                        processId={processId}
                      />
                    </div>
                  </div>
                </>
              ) : mode === MODES.CODE ? (
                <div className="screen-workshop__code">
                  <div>
                    <div className="canvas-editor__section-title">
                      {labels.HTML}
                    </div>
                    <Input
                      type="textarea"
                      rows={14}
                      className="canvas-editor__html-input"
                      value={code.html}
                      onChange={(e) =>
                        setCode((current) => ({
                          ...current,
                          html: e.target.value,
                        }))
                      }
                    />
                  </div>
                  {active ? (
                    <div className="canvas-editor__meta">
                      {labels.CODE_STATE_HINT}
                    </div>
                  ) : (
                    <div>
                      <div className="canvas-editor__section-title">
                        {labels.CSS}
                      </div>
                      <Input
                        type="textarea"
                        rows={10}
                        className="canvas-editor__html-input"
                        value={code.css}
                        onChange={(e) =>
                          setCode((current) => ({
                            ...current,
                            css: e.target.value,
                          }))
                        }
                      />
                    </div>
                  )}
                  {!screenVersionId ? (
                    <div className="canvas-editor__meta">
                      {labels.CODE_CREATE_HINT}
                    </div>
                  ) : null}
                  <div className="canvas-editor__row">
                    <Button
                      type="primary"
                      loading={savingCode}
                      disabled={busy || (!screenVersionId && !code.html.trim())}
                      onClick={handleSaveCode}
                    >
                      {screenVersionId ? labels.SAVE : labels.CODE_CREATE}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="screen-workshop__preview">
                  <ScreenThumbnail
                    html={shownHtml || ""}
                    styleContent={styleContent}
                    size="lg"
                    scrollable
                    highlightSlot={focused.map((item) => item.id)}
                    onSlotSelect={picking ? focusOn : null}
                    title={title}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

ScreenWorkshop.propTypes = {
  open: PropTypes.bool,
  processId: PropTypes.string,
  onClose: PropTypes.func,
  title: PropTypes.string,
  screenVersionId: PropTypes.string,
  statusId: PropTypes.string,
  screenId: PropTypes.string,
  versionNumber: PropTypes.number,
  htmlContent: PropTypes.string,
  styleContent: PropTypes.string,
  slots: PropTypes.array,
  manifestId: PropTypes.string,
  states: PropTypes.array,
  variables: PropTypes.array,
  conditionLabels: PropTypes.object,
  published: PropTypes.bool,
  publishBlocked: PropTypes.bool,
  labels: PropTypes.object,
  reload: PropTypes.func,
};

ScreenWorkshop.defaultProps = {
  open: false,
  processId: undefined,
  onClose: undefined,
  title: undefined,
  screenVersionId: null,
  statusId: null,
  screenId: null,
  versionNumber: undefined,
  htmlContent: "",
  styleContent: undefined,
  slots: [],
  manifestId: null,
  states: [],
  variables: [],
  conditionLabels: {},
  published: false,
  publishBlocked: false,
  labels: {},
  reload: undefined,
};

export default ScreenWorkshop;
